import React, { useEffect, useId, useRef } from 'react';

import styles from './Flag.scss';

const STRIPS = 24;
const FLAG_WIDTH = 120;
const FLAG_HEIGHT = 80;
const STRIP_WIDTH = FLAG_WIDTH / STRIPS;
const WAVE_AMPLITUDE = 3;
const DURATION = 1.5;

export const Flag = () => {
  const uid = useId();
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion && typeof svg.pauseAnimations === 'function') {
      svg.pauseAnimations();
    }
  }, []);

  const strips = Array.from({ length: STRIPS }, (_, i) => {
    const x = i * STRIP_WIDTH;
    const delay = (i / STRIPS) * DURATION;
    const clipId = `${uid}-strip-${i}`;

    return (
      <g key={i}>
        <clipPath id={clipId}>
          <rect height={FLAG_HEIGHT} shapeRendering="crispEdges" width={STRIP_WIDTH + 1} x={x} y={0} />
        </clipPath>

        <g clipPath={`url(#${clipId})`} shapeRendering="crispEdges">
          <g>
            <animateTransform
              additive="sum"
              attributeName="transform"
              begin={`${delay}s`}
              dur={`${DURATION}s`}
              repeatCount="indefinite"
              type="translate"
              values={`0,0; 0,${WAVE_AMPLITUDE}; 0,0; 0,${-WAVE_AMPLITUDE}; 0,0`}
            />

            {/* White background – oversized vertically to cover gaps during animation */}
            <rect fill="#ffffff" height={FLAG_HEIGHT + WAVE_AMPLITUDE * 2} width={FLAG_WIDTH} x={0} y={-WAVE_AMPLITUDE} />

            {/* Red cross – horizontal */}
            <rect fill="#CE1124" height={16} width={FLAG_WIDTH} x={0} y={32} />

            {/* Red cross – vertical – oversized to match white background */}
            <rect fill="#CE1124" height={FLAG_HEIGHT + WAVE_AMPLITUDE * 2} width={16} x={52} y={-WAVE_AMPLITUDE} />
          </g>
        </g>
      </g>
    );
  });

  return (
    <span className={styles.flag}>
      <svg
        ref={svgRef}
        aria-label="England flag"
        role="img"
        style={{ overflow: 'hidden' }}
        viewBox={`0 0 ${FLAG_WIDTH} ${FLAG_HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {strips}
      </svg>
    </span>
  );
};
